import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Scale, MessageSquarePlus, Edit3, ShieldAlert } from "lucide-react";

const P2_FIELDS = [
  { k: "p2_employer", label: "Employer", type: "text" },
  { k: "p2_annual_income", label: "Annual income", type: "text" },
  { k: "p2_self_employed", label: "Self-employed / business owner?", type: "yesno" },
  { k: "p2_owns_business", label: "Own a business / practice?", type: "yesno" },
  { k: "p2_owns_real_estate", label: "Own real estate / land?", type: "yesno" },
  { k: "p2_has_retirement", label: "Retirement accounts / pensions?", type: "yesno" },
  { k: "p2_has_investments", label: "Investments / savings?", type: "yesno" },
  { k: "p2_has_inheritance", label: "Inheritances or expected inheritance?", type: "yesno" },
  { k: "p2_separate_property", label: "Separate property to protect?", type: "text" },
  { k: "p2_has_debts", label: "Any debts?", type: "yesno" },
];

export default function Review() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [needPw, setNeedPw] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [entered, setEntered] = useState(false);
  const [p2, setP2] = useState({});

  useEffect(() => {
    document.title = "Review, NuPDocs";
    const meta = document.createElement("meta"); meta.name = "robots"; meta.content = "noindex,nofollow"; document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const load = async () => {
    try { const { data } = await api.post(`/review/${token}`, { password: pw }); setData(data); }
    catch (e) { if (e.response?.status === 401) setNeedPw(true); else setErr(fmtErr(e.response?.data?.detail) || "Link unavailable"); }
  };
  useEffect(() => { load(); }, [token]);
  useEffect(() => { if (data?.party2_answers) setP2(data.party2_answers); }, [data]);

  if (err) return <Center>{err}</Center>;
  if (needPw && !data) return (
    <Center>
      <div className="text-center">
        <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-4">Password required</h1>
        <input type="password" className="border border-[#E2E8F0] rounded-lg px-4 py-2 mb-3" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="review-pw" />
        <button onClick={load} className="block mx-auto bg-[#0B132B] text-white px-6 py-2 rounded-full">Unlock</button>
      </div>
    </Center>
  );
  if (!data) return <Center>Loading…</Center>;

  const { share, matter, version, rules, comments, suggestions, clause_flags = [] } = data;
  const canComment = ["comment", "suggest", "edit"].includes(share.permission);
  const canSuggest = ["suggest", "edit"].includes(share.permission);

  if (!entered) return (
    <Center>
      <div className="text-center max-w-sm">
        <Scale className="w-8 h-8 text-[#0B132B] mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">You've been invited to review a marital agreement</h1>
        <p className="text-sm text-[#5C6B73] mt-2">{matter.title}. Access level: <strong className="capitalize">{share.permission}</strong>. No account needed.</p>
        <input className="border border-[#E2E8F0] rounded-lg px-4 py-2 mt-4 w-full" placeholder="Your name (for attribution)" value={name} onChange={(e) => setName(e.target.value)} data-testid="reviewer-name" />
        <button onClick={() => { if (!name.trim()) return toast.error("Enter your name"); setEntered(true); }} data-testid="review-enter" className="mt-3 bg-[#0B132B] text-white px-6 py-2.5 rounded-full font-medium w-full">Review Document</button>
      </div>
    </Center>
  );

  const addComment = async (sectionId) => {
    const text = prompt("Comment for " + sectionId); if (!text) return;
    try { await api.post(`/review/${token}/comment`, { section_id: sectionId, text, author_name: name }); toast.success("Comment added"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const addSuggestion = async (sectionId, original) => {
    const suggested = prompt("Suggested replacement text for " + sectionId, original); if (!suggested) return;
    try { await api.post(`/review/${token}/suggest`, { section_id: sectionId, original: original.slice(0, 200), suggested, author_name: name }); toast.success("Suggestion submitted"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const saveP2 = async () => {
    try { await api.post(`/review/${token}/party2`, { answers: p2, author_name: name }); toast.success("Your financial section was saved"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#E2E8F0]"><div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between"><div className="flex items-center gap-2"><Scale className="w-6 h-6 text-[#0B132B]" /><span className="font-display text-lg font-semibold text-[#0B132B]">Attorney Review Mode</span></div><span className="text-xs text-[#5C6B73]">{name} · {share.permission} access</span></div></header>
      <div className="max-w-5xl mx-auto px-5 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">{matter.title}</h1>
          <p className="text-xs text-[#5C6B73] mb-4">{matter.doc_type} · {matter.state} · Status: {matter.status}</p>
          <div className="border border-[#E2E8F0] rounded-xl p-5 mb-6 bg-[#F8F9FA]" data-testid="reviewer-risk-panel">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-4 h-4 text-[#4A6FA5]" />
              <h3 className="font-semibold text-[#0B132B]">Clause risk review</h3>
            </div>
            {clause_flags.length === 0 ? (
              <p className="text-sm text-[#5C6B73]">No enforceability risks were flagged for the clauses selected in this draft under {matter.state || "the selected"} law.</p>
            ) : (
              <>
                <p className="text-xs text-[#5C6B73] mb-3">Automated checks flagged {clause_flags.length} item{clause_flags.length > 1 ? "s" : ""} to review under {matter.state || "the selected state's"} law. This is legal information, not legal advice.</p>
                <div className="space-y-3">
                  {clause_flags.map((f) => (
                    <div key={f.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`risk-flag-${f.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#0B132B]">{f.title}</span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${f.severity === "high" ? "bg-red-100 text-red-800" : f.severity === "medium" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{f.severity}</span>
                      </div>
                      <p className="mt-1 text-sm text-[#0B132B] leading-relaxed">{f.message}</p>
                      {f.recommendation && <p className="mt-1 text-xs text-[#4A6FA5]">Suggested: {f.recommendation}</p>}
                      {f.citation && <p className="mt-1 text-[11px] text-[#5C6B73]">Source: {f.citation}</p>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="border border-[#E2E8F0] rounded-xl p-6 grain">
            {!version && <p className="text-[#5C6B73]">No draft generated yet.</p>}
            {version?.sections.map((s) => (
              <section key={s.id} className="mb-5 group" data-testid={`review-section-${s.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#0B132B]">{s.heading}</h3>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canComment && <button onClick={() => addComment(s.id)} data-testid={`comment-${s.id}`} className="text-[#4A6FA5]"><MessageSquarePlus className="w-4 h-4" /></button>}
                    {canSuggest && <button onClick={() => addSuggestion(s.id, s.body)} data-testid={`suggest-${s.id}`} className="text-[#4A6FA5]"><Edit3 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <p className="text-sm text-[#0B132B] leading-relaxed whitespace-pre-wrap mt-1">{s.body}</p>
                {comments.filter((c) => c.section_id === s.id).map((c) => <div key={c.id} className="mt-2 bg-blue-50 border border-blue-100 rounded p-2 text-xs text-[#0B132B]"><strong>{c.author_name}:</strong> {c.text}</div>)}
              </section>
            ))}
          </div>
          {share.role === "partner" && (
            <div className="border border-[#4A6FA5]/40 bg-[#EDF1F7] rounded-xl p-6 mt-6" data-testid="party2-section">
              <h3 className="font-semibold text-[#0B132B]">Your financial disclosure (Party 2)</h3>
              <p className="text-sm text-[#5C6B73] mb-4">Only you can enter your own financial information. It becomes part of the shared agreement's disclosure and is attributed to you.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {P2_FIELDS.map((f) => (
                  <div key={f.k}>
                    <label className="block text-xs text-[#5C6B73] mb-1">{f.label}</label>
                    {f.type === "text" ? (
                      <input className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" data-testid={`p2-${f.k}`} value={p2[f.k] || ""} onChange={(e) => setP2({ ...p2, [f.k]: e.target.value })} />
                    ) : (
                      <div className="flex gap-2">{["Yes", "No"].map((o) => <button key={o} type="button" onClick={() => setP2({ ...p2, [f.k]: o })} data-testid={`p2-${f.k}-${o}`} className={`flex-1 text-sm border rounded-lg py-2 ${p2[f.k] === o ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B] bg-white"}`}>{o}</button>)}</div>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={saveP2} data-testid="save-party2" className="mt-4 bg-[#0B132B] text-white px-5 py-2.5 rounded-full text-sm font-medium">Save my section</button>
            </div>
          )}
        </div>
        <aside>
          <h3 className="font-semibold text-[#0B132B] mb-2">State law sources</h3>
          <div className="space-y-2 mb-6">{rules.slice(0, 5).map((r) => <div key={r.id} className="border border-[#E2E8F0] rounded p-2 text-xs"><strong className="text-[#0B132B]">{r.category}</strong><div className="text-[#5C6B73]">{r.source}</div></div>)}</div>
          <h3 className="font-semibold text-[#0B132B] mb-2">Proposed changes</h3>
          <div className="space-y-2">{suggestions.length === 0 && <p className="text-xs text-[#5C6B73]">None yet.</p>}{suggestions.map((s) => <div key={s.id} className="border border-[#E2E8F0] rounded p-2 text-xs"><div className="text-[#5C6B73] line-through">{s.original}</div><div className="text-[#0B132B]">{s.suggested}</div><div className="text-[#4A6FA5]">{s.status} · {s.author_name}</div></div>)}</div>
        </aside>
      </div>
    </div>
  );
}

function Center({ children }) { return <div className="min-h-screen marketing-bg flex items-center justify-center px-5"><div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-[#5C6B73]">{children}</div></div>; }
